const SERVICES = [
  {
    title: "App Development",
    subtitle: "Mobile & Desktop Utilities",
    desc: "We build high-performance utility applications — system cleaners, optimizers, file managers, and AI-powered tools — for iOS, Android, macOS, and Windows. Our team handles the entire cycle: architecture, UI/UX design, development, QA, and release.",
    icon: "ri-smartphone-line",
    features: [
      "iOS & Android native development",
      "macOS & Windows desktop apps",
      "AI/ML integration into utilities",
      "Cross-platform solutions",
    ],
    image: "https://readdy.ai/api/search-image?query=Abstract%20digital%20composition%20representing%20software%20development%20process%2C%20flowing%20code%20elements%20and%20geometric%20patterns%2C%20warm%20amber%20and%20cream%20tones%2C%20modern%20minimalist%20tech%20aesthetic%2C%20clean%20structured%20design%20with%20interconnected%20nodes%2C%20high%20detail%2C%20harmonious%20composition%2C%20sophisticated%20corporate%20style&width=800&height=600&seq=service-dev-intellivex&orientation=landscape",
  },
  {
    title: "IT Consulting",
    subtitle: "Technology Strategy & Advisory",
    desc: "We help teams make informed technology decisions, optimize development processes, and implement modern tools. From architecture reviews to technology stack selection — we guide you toward scalable and maintainable solutions.",
    icon: "ri-brain-line",
    features: [
      "Architecture & tech stack audits",
      "Process optimization & automation",
      "Team scaling strategies",
      "Security & compliance guidance",
    ],
    image: "https://readdy.ai/api/search-image?query=Abstract%20representation%20of%20strategic%20consulting%20and%20technology%20advisory%2C%20clean%20geometric%20shapes%20connecting%20like%20a%20network%20of%20decisions%2C%20warm%20moss%20green%20and%20cream%20palette%2C%20professional%20corporate%20aesthetic%2C%20flowing%20structured%20composition%2C%20editorial%20design%20quality%2C%20high%20detail%2C%20minimalist%20modern%20style&width=800&height=600&seq=service-consult-intellivex&orientation=landscape",
  },
  {
    title: "Digital Marketing",
    subtitle: "App Promotion & User Acquisition",
    desc: "We design and execute user acquisition strategies, strengthen app store presence, boost brand awareness, and help applications find their audience among millions of competitors. ASO, media buying, and scaling — all under one roof.",
    icon: "ri-megaphone-line",
    features: [
      "App Store Optimization (ASO)",
      "Media buying & paid UA campaigns",
      "Creative production & A/B testing",
      "Analytics & retention optimization",
    ],
    image: "https://readdy.ai/api/search-image?query=Abstract%20digital%20marketing%20and%20advertising%20visualization%2C%20dynamic%20ascending%20charts%20and%20target%20elements%2C%20warm%20amber%20and%20terracotta%20tones%2C%20modern%20clean%20composition%20representing%20growth%20and%20audience%20reach%2C%20sophisticated%20corporate%20aesthetic%2C%20high%20detail%2C%20harmonious%20energetic%20yet%20professional%20design&width=800&height=600&seq=service-marketing-intellivex&orientation=landscape",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative w-full bg-cream-100 py-24 md:py-32 border-y border-ink-950/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-2 h-2 rounded-full bg-moss-500"></span>
              <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-ink-700">
                Our Services
              </span>
            </div>
            <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl lg:text-5xl">
              Full-cycle development
              <br />
              and promotion
            </h2>
          </div>
          <p className="max-w-md text-sm md:text-base text-ink-700 leading-relaxed">
            Three interconnected directions that cover the entire lifecycle of a utility application —
            from the first line of code to millions of users.
          </p>
        </div>

        {/* Service cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {SERVICES.map((svc) => (
            <div
              key={svc.title}
              className="group flex flex-col rounded-2xl bg-cream-50 border border-ink-950/10 overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              {/* Image */}
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                <img
                  src={svc.image}
                  alt={svc.title}
                  className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cream-50 via-transparent to-transparent"></div>
              </div>

              {/* Content */}
              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100">
                    <i className={`${svc.icon} text-lg text-amber-600`}></i>
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-xl text-ink-950">{svc.title}</h3>
                    <p className="text-[11px] uppercase tracking-widest text-ink-500 font-medium">
                      {svc.subtitle}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-ink-700 leading-relaxed mb-5">
                  {svc.desc}
                </p>

                <div className="mt-auto pt-5 border-t border-ink-950/5">
                  <ul className="space-y-2.5">
                    {svc.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm text-ink-700">
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-moss-100 shrink-0">
                          <i className="ri-check-line text-[10px] text-moss-600"></i>
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}